import { useMutation, useQuery } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { sortBy } from 'lodash';
import { type EnqueueSnackbar, useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';

import { DOCS_URL } from '@/constants/common';
import { useHandleError } from '@/hooks/useHandleError';
import { customEventAtom } from '@/pages/Administration/Devices';
import { formatSourceName } from '@/pages/Administration/Devices/Devices.service';
import devicesAtom, {
  CustomEventTypes,
  selectedDeviceIdAtom
} from '@/pages/Administration/Devices/Devices.store';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { axiosClient, queryClient } from '@/services/BaseApi.service';

import { logForwarderLinks } from '../SourceCreate/available-doc-links.constant';
import SOURCE_TYPE from '../SourceCreate/SourceCreate.constants';
import { selectedSourceTypeIdAtom } from '../SourceCreate/SourceCreate.store';
import {
  editedLogSourcesAtom,
  filterSourceAtom,
  hostNameAtom,
  logSourcesAtom,
  logSourcesDialogModeAtom,
  logSourcesDialogOpenAtom,
  orgNameAtom,
  savingInProgressAtom,
  searchQueryAtom
} from './LogSourcesCreate.store';
import {
  type DataStreamModel,
  HTTP_STATUS,
  type LogSourceConfigDTO,
  type LogSourceModel,
  type LogSourcesDialogMode,
  type LogSourcesResponseDTO
} from './LogSourcesCreate.types';
import { exactMatch, wildcardMatch } from './LogSourcesCreate.utils';

const showSnackbar = (mode: string, enqueueSnackbar: EnqueueSnackbar) => {
  let action = '';
  switch (mode) {
    case 'AddLogSources':
      action = 'Log Sources Added';
      break;
    case 'EditLogSources':
      action = 'Log Sources Updated';
      break;
    default:
      action = 'Log Sources Added';
  }
  enqueueSnackbar(`${action} Successfully`, { variant: 'success' });
};

export const useLogSourcesDialog = () => {
  const [error, setError] = useState('');
  const [mode, setMode] = useAtom(logSourcesDialogModeAtom);
  const formMethods = useForm();
  const { setValue, clearErrors, reset } = formMethods;
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useAtom(logSourcesDialogOpenAtom);
  const [, setCustomEvent] = useAtom(customEventAtom);
  const [deviceId, setDeviceId] = useAtom(selectedDeviceIdAtom);
  const [selectedSourceTypeId, setSelectedSourceTypeId] = useAtom(
    selectedSourceTypeIdAtom
  );
  const [logSources, setLogSources] = useAtom(logSourcesAtom);
  const [, setEditedLogSources] = useAtom(editedLogSourcesAtom);
  const [savingInProgress, setSavingInProgress] = useAtom(savingInProgressAtom);

  const openLogSourcesDialog = (
    dialogMode: LogSourcesDialogMode = 'AddLogSources',
    device_uid: string = ''
  ) => {
    // Set default source type ID for log sources
    setSelectedSourceTypeId(SOURCE_TYPE.LOG_SOURCES);
    setDeviceId(device_uid);
    setMode(dialogMode);
    setOpen(true);
  };

  const clearApiError = () => {
    setError('');
  };

  const closeLogSourcesDialog = () => {
    clearApiError();
    setOpen(false);
  };

  const clearAllLogSourcesSelections = () => {
    setEditedLogSources(null);
    setLogSources((prev) =>
      prev.map((item) => ({
        ...item,
        isSelected: false,
        data_streams:
          item.data_streams?.map((stream) => ({
            ...stream,
            isSelected: false
          })) || []
      }))
    );
    // Clearing all the configuration fields
    logSources?.forEach((item) => {
      const configKey = `${item.name}_configuration`;
      setValue(`${configKey}`, '');

      const tzKey = `${item.name}_timezone`;
      setValue(`${tzKey}`, '');
    });
    clearErrors();
    clearApiError();
    reset();
  };

  const mutation = useMutation({
    mutationFn: (src: LogSourceConfigDTO) => {
      setSavingInProgress(true);
      const payload = src;
      return axiosClient.post(
        `/devices/${deviceId || ''}/${selectedSourceTypeId}/log_sources`,
        payload
      );
    },
    onSuccess: (data) => {
      setSavingInProgress(false);
      if (data.status === 200 || data.status === 201) {
        closeLogSourcesDialog();
        showSnackbar(mode, enqueueSnackbar);
        setCustomEvent(CustomEventTypes.REFRESH_DEVICE_LISTING);
        clearAllLogSourcesSelections();
      } else {
        setError(
          `Something is wrong,\n ${data?.data?.message}\n Please try again.`
        );
      }
    },
    onError: (err: Error | AxiosError) => {
      setSavingInProgress(false);
      if (axios.isAxiosError(err)) {
        setError(`${err?.response?.data?.detail || 'Error saving log source'}`);
      } else {
        setError(`Error saving log source (${err.message})`);
      }
    }
  });

  return {
    open,
    error,
    mutation,
    mode,
    setError,
    savingInProgress,
    clearAllLogSourcesSelections,
    openLogSourcesDialog,
    closeLogSourcesDialog,
    clearApiError
  };
};

// Fetch Log Sources API call
export const useFetchLogSources = () => {
  const [, setLogSources] = useAtom(logSourcesAtom);
  // Fetching log forwarders log sources (device type 2)
  const logSourcesQuery = useQuery<LogSourcesResponseDTO[]>({
    queryKey: [`device_type/2/log_sources`],
    staleTime: Infinity
  });

  useEffect(() => {
    if (logSourcesQuery.isError) {
      setLogSources([]);
      return;
    }

    if (logSourcesQuery.isSuccess) {
      const logSourcesData = logSourcesQuery.data || [];
      if (logSourcesData.length > 0) {
        const { connection_input_schema } = logSourcesData[0] || {};
        const modules = connection_input_schema?.modules as
          | LogSourceModel[]
          | undefined;

        if (modules) {
          const logSources = modules
            // Filter out modules that ONLY have cloud_api type at module level
            .filter((module) => {
              // If module has type field and it's cloud_api, exclude it
              if (module?.type === 'cloud_api') {
                return false;
              }
              return true;
            })
            .map((module) => {
              // Safe string operations with fallbacks
              const vendor = module?.vendor || 'Unknown';
              const product = module?.product || 'Unknown';
              const vendorLower = vendor.toLowerCase();
              const productLower = product.toLowerCase();
              const moduleName = module?.name?.toLowerCase();
              const link = moduleName
                ? logForwarderLinks[
                    moduleName as keyof typeof logForwarderLinks
                  ]
                : undefined;
              const docLink = link ? `${DOCS_URL}${link}` : '';

              return {
                ...module,
                display_name:
                  productLower !== vendorLower
                    ? `${formatSourceName(vendor)} - ${formatSourceName(product)}`
                    : formatSourceName(vendor),
                isSelected: false,
                doc_link: docLink,
                data_streams:
                  module?.data_streams
                    ?.filter((ds) => ds?.type !== 'cloud_api')
                    .map(
                      ({
                        preprocessor: _preprocessor,
                        processor: _processor,
                        ...rest
                      }) => {
                        const display_name = formatSourceName(
                          rest.name || 'Unknown'
                        );
                        return {
                          ...rest,
                          display_name,
                          isSelected: false
                        } as DataStreamModel;
                      }
                    ) || []
              } as LogSourceModel;
            });
          setLogSources(logSources);
        } else {
          setLogSources([]);
        }
      } else {
        setLogSources([]);
      }
    }
  }, [
    logSourcesQuery.data,
    logSourcesQuery.isError,
    logSourcesQuery.isSuccess,
    logSourcesQuery.isLoading,
    setLogSources
  ]);

  return logSourcesQuery;
};

// Fetch Log Sources by deviceuid API call
export const useFetchLogSourcesByDeviceUid = () => {
  const [deviceId] = useAtom(selectedDeviceIdAtom);
  const [, setEditedLogSources] = useAtom(editedLogSourcesAtom);
  const logSourcesByDeviceIdQuery = useQuery({
    queryKey: [
      `devices/${deviceId || ''}/${SOURCE_TYPE.LOG_SOURCES}/log_sources`
    ],
    enabled: !!deviceId
  });

  useEffect(() => {
    //  Fetching Fresh data always and avoid from cache
    if (
      logSourcesByDeviceIdQuery.isSuccess &&
      !logSourcesByDeviceIdQuery.isRefetching &&
      logSourcesByDeviceIdQuery.isFetchedAfterMount
    ) {
      const data = logSourcesByDeviceIdQuery?.data as LogSourceConfigDTO;
      if (data?.sources) {
        setEditedLogSources(data);
      } else {
        setEditedLogSources(null);
      }
    }
  }, [
    logSourcesByDeviceIdQuery.isSuccess,
    logSourcesByDeviceIdQuery.isRefetching,
    logSourcesByDeviceIdQuery.isFetchedAfterMount,
    setEditedLogSources
  ]);

  return logSourcesByDeviceIdQuery;
};

// Custom hook for Log Sources form
export const useLogSourcesForm = () => {
  const { reset, setValue, clearErrors } = useFormContext();
  const [deviceId] = useAtom(selectedDeviceIdAtom);
  const [logSources, setLogSources] = useAtom(logSourcesAtom);
  const [searchQuery, setSearchQuery] = useAtom(searchQueryAtom);
  const [filter, setFilter] = useAtom(filterSourceAtom);
  const [, setHostName] = useAtom(hostNameAtom);
  const [, setOrgName] = useAtom(orgNameAtom);
  const [editedLogSources] = useAtom(editedLogSourcesAtom);
  const [devices] = useAtom(devicesAtom);

  // Triggering the API call to fetch log sources
  const logSourcesQuery = useFetchLogSources();
  const logSourcesByDeviceIdQuery = useFetchLogSourcesByDeviceUid();

  // Select products and data streams based on sources
  const selectProductsAndStreams = useCallback(
    (sources: string[]) => {
      // Update log sources state with selections
      setLogSources((prev) =>
        prev.map((item) => {
          // Check if product matches a wildcard pattern
          const productWildcardMatch = sources.some(
            (source) => source.endsWith('*') && wildcardMatch(source, item.name)
          );

          // Check if product is explicitly listed (without wildcard)
          const productExplicitMatch = sources.some(
            (source) => source === item.name.toLowerCase()
          );

          const productSelected = productWildcardMatch || productExplicitMatch;

          // Process data streams
          const updatedDataStreams =
            item.data_streams?.map((stream) => {
              // Stream is selected if:
              // 1. Parent product has wildcard match
              // 2. Stream name exactly matches a source entry
              const streamSelected =
                productWildcardMatch ||
                sources.some((source) => exactMatch(source, stream.name));

              return {
                ...stream,
                isSelected: streamSelected,
                selectionSource: streamSelected ? 'auto-populated' : 'unchanged'
              } as DataStreamModel;
            }) ?? [];

          // Determine parent state based on streams
          const hasStreams = updatedDataStreams.length > 0;
          const allStreamsSelected =
            hasStreams && updatedDataStreams.every((s) => s.isSelected);
          const someStreamsSelected =
            hasStreams && updatedDataStreams.some((s) => s.isSelected);

          let parentIsSelected = false;
          let parentSelectionSource: 'auto-populated' | 'unchanged' =
            'unchanged';

          if (hasStreams) {
            parentIsSelected = allStreamsSelected;
            parentSelectionSource = productWildcardMatch
              ? 'auto-populated'
              : 'unchanged';
          } else {
            parentIsSelected = productSelected;
            parentSelectionSource = productSelected
              ? 'auto-populated'
              : 'unchanged';
          }

          return {
            ...item,
            isSelected: parentIsSelected,
            streamsPartlySelected:
              hasStreams && someStreamsSelected && !allStreamsSelected,
            data_streams: updatedDataStreams,
            timeZone: 'Select Timezone',
            selectionSource: parentSelectionSource
          };
        })
      );
    },
    [setLogSources]
  );

  // Handler for device type changes.
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setSearchQuery(value.toLowerCase());
    },
    [setSearchQuery]
  );

  // Disable Enter key to prevent form submission
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    },
    []
  );

  // Toggle filter between all and selected
  const toggleFilter = () =>
    setFilter((prev) => (prev === 'all' ? 'selected' : 'all'));

  // Filtering log sources based on search query and filter
  const filteredLogSources = useMemo(() => {
    if (!logSources) return [];

    const searchLower = searchQuery.toLowerCase();

    // Filter the log sources
    const filtered = logSources.filter((source: LogSourceModel) => {
      const matchesFilter =
        filter === 'all' ||
        source?.isSelected ||
        source?.data_streams?.some(
          (stream: DataStreamModel & { isSelected?: boolean }) =>
            stream?.isSelected
        );

      if (!matchesFilter) return false;

      // Early return if no search query
      if (!searchQuery) return true;

      const matchesSearch = JSON.stringify(source)
        .toLowerCase()
        .includes(searchLower);

      return matchesFilter && matchesSearch;
    });

    // sort the filtered log sources by display name
    return sortBy(filtered, ['display_name']);
  }, [logSources, filter, searchQuery]);

  // Setting form values based on the edited log sources
  useEffect(() => {
    // Apply selections when data is ready
    if (!logSourcesQuery.isSuccess || !editedLogSources) {
      return;
    }

    if (editedLogSources?.sources) {
      // Setting selected products and streams
      selectProductsAndStreams(editedLogSources?.sources as string[]);

      // Setting source configuration fields
      logSources?.forEach((item) => {
        setValue(`${item.name}_configuration`, '');
        setValue(`${item.name}_timezone`, 'Select Timezone');
      });

      if (editedLogSources?.source_configs) {
        Object.entries(editedLogSources.source_configs).forEach(
          ([key, config]) => {
            const configStr = JSON.stringify(config);
            setValue(`${key}_configuration`, configStr);

            const timeZone = (config as { timezone?: string })?.timezone;
            if (timeZone) {
              setLogSources((prev) =>
                prev.map((item) =>
                  item.name === key ? { ...item, timeZone } : item
                )
              );
            }
          }
        );
      }
    }

    // Setting input configuration
    if (editedLogSources?.input_configs) {
      const configString =
        Object.keys(editedLogSources.input_configs).length > 0
          ? JSON.stringify(editedLogSources.input_configs)
          : '';

      setValue('input_configuration', configString);
    }
  }, [
    editedLogSources,
    logSourcesQuery.isSuccess,
    selectProductsAndStreams,
    setValue,
    setLogSources
  ]);

  // Clearing form values and errors on component mount
  useEffect(() => {
    // Initialize form on mount
    clearErrors();
    reset();
    setFilter('all');
    setSearchQuery('');
  }, [clearErrors, reset, setFilter, setSearchQuery]);

  useEffect(() => {
    // Update device info when deviceId changes
    const device = devices?.find((d) => d.device_uid === deviceId);
    setHostName(device?.display_name || device?.host_name || '');
    setOrgName(device?.organization?.name || '');
  }, [deviceId, devices, setHostName, setOrgName]);

  return {
    logSourcesQuery,
    logSourcesByDeviceIdQuery,
    filteredLogSources,
    handleSearchChange,
    handleKeyDown,
    toggleFilter
  };
};

export const useClearDeviceStatus = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();

  const clearDeviceStatusMutation = useMutation({
    mutationFn: ({ host_name }: { host_name: string; device_uid: string }) => {
      return axiosClient.post(`/devices/status`, {
        body: '!~~~{ "status_code": 2001, "status_from": "device"}~~~!%%%',
        link: '',
        title: 'Clearing error from scope UI',
        host_name,
        last_updated: `${Date.now()}`,
        alert_transition: 'Clear'
      });
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failed to reset device status');
    },
    onSuccess: (data, variables) => {
      if (
        data.status === HTTP_STATUS.OK ||
        data.status === HTTP_STATUS.CREATED
      ) {
        enqueueSnackbar('Log Sources Status Cleared Successfully', {
          variant: 'success'
        });
      }
      if (variables.device_uid) {
        queryClient.invalidateQueries({
          queryKey: [`devices/${variables.device_uid}`]
        });
      }
      queryClient.invalidateQueries({
        queryKey: [`organizations/${selectedOrganization.label}/devices`]
      });
    }
  });

  return { clearDeviceStatusMutation };
};
